import useFormatter from "@/hooks/use-formatter.hook.ts";
import useRoleEditService from "@/pages/settings/roles/edit/role-edit-service.ts";
import { useTimeOffEmployeePolicyChecks } from "@/queries/use-time-off.query.ts";
import { ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import { Rule, RuleConditionType } from "@/types/rule.types.ts";
import {
  ApiTimeOffCategoryShort,
  ApiTimeOffEmployeePolicyCheck,
} from "@/types/time-off/time-off-policy.types.ts";
import {
  ButtonLink,
  FontAwesomeIcon,
  ListItem,
  Modal,
  ModalProps,
  Select,
  Skeleton,
  Stack,
  Tag,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";

export interface TimeOffPolicyChecksModalProps extends ModalProps {
  employee: ApiEmployeeShort;
  category?: Pick<ApiTimeOffCategoryShort, "id" | "title">;
  categories?: Pick<ApiTimeOffCategoryShort, "id" | "title">[];
}

export function TimeOffPolicyChecksModal({
  employee,
  category,
  categories,
  ...props
}: TimeOffPolicyChecksModalProps) {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const { getApplyCardTitle, getAssignCardDescription, getSignTitle } = useRoleEditService();
  const [selectedCategoryId, setSelectedCategoryId] = useState(category?.id);
  const { data: checks, isPending } = useTimeOffEmployeePolicyChecks(
    employee.id,
    selectedCategoryId
  );
  const isNoneAssigned = !!checks && !checks.some((check) => check.is_assigned);
  const firstMatchedIndex = checks?.findIndex((check) => check.is_matched) ?? -1;

  const getRuleTitle = (rule: Rule) => {
    const title = getApplyCardTitle(rule.condition_type);
    const description =
      rule.condition_type === RuleConditionType.employee
        ? `${getSignTitle(rule.sign)}${t("plurals:employees_count", { count: rule.condition_ids.length })}`
        : getAssignCardDescription(rule);
    return description ? `${title} · ${description}` : title;
  };

  const renderRules = (check: ApiTimeOffEmployeePolicyCheck) => {
    const groups = check.groups.filter((group) => group.rules.length > 0);
    const rulesCount = groups.reduce((count, group) => count + group.rules.length, 0);
    if (!check.is_effective || !rulesCount) return undefined;

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Text
            className={"text-secondary text-sm cursor-help"}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            {t("time_off:policy_check_rules_count", { count: rulesCount })}
          </Text>
        </TooltipTrigger>
        <TooltipContent className={"px-3 py-2"}>
          <Stack gap={"xs"}>
            {groups.map((group, groupIndex) => (
              <Fragment key={groupIndex}>
                {groupIndex > 0 && <Text className={"text-brand-light"}>{t("common:or")}</Text>}
                {group.rules.map(({ rule, is_matched }, ruleIndex) => (
                  <Fragment key={ruleIndex}>
                    {ruleIndex > 0 && <Text className={"text-brand-light"}>{t("common:and")}</Text>}
                    <Text className={is_matched ? "text-brand-light" : "text-warning"}>
                      {getRuleTitle(rule)}
                    </Text>
                  </Fragment>
                ))}
              </Fragment>
            ))}
          </Stack>
        </TooltipContent>
      </Tooltip>
    );
  };

  const getMatchedTitle = (index: number) =>
    index === firstMatchedIndex
      ? t("time_off:policy_check_pending")
      : t("time_off:policy_check_matched");

  const renderStatus = (check: ApiTimeOffEmployeePolicyCheck, index: number) => {
    if (check.is_assigned) {
      return (
        <Tag variant={"positive"} className={"whitespace-nowrap"}>
          {t("time_off:policy_check_assigned")}
        </Tag>
      );
    }
    if (!check.is_effective) {
      return (
        <Tag variant={"secondary"} className={"whitespace-nowrap"}>
          {t("time_off:policy_check_not_effective")}
        </Tag>
      );
    }
    return (
      <Tag variant={"secondary"} className={"whitespace-nowrap"}>
        {check.is_matched ? getMatchedTitle(index) : t("time_off:policy_check_not_matched")}
      </Tag>
    );
  };

  const renderHint = (checks: ApiTimeOffEmployeePolicyCheck[]) => {
    if (!checks.length) return t("time_off:policy_checks_no_policies");
    if (isNoneAssigned && !checks.some((check) => check.is_matched)) {
      return t("time_off:policy_checks_none_matched");
    }
    return t("time_off:policies_order_hint");
  };

  return (
    <Modal
      title={
        isNoneAssigned ? t("time_off:policy_checks_title_none") : t("time_off:policy_checks_title")
      }
      caption={
        category
          ? `${renderEmployeeName(employee)} · ${category.title}`
          : renderEmployeeName(employee)
      }
      {...props}
      withCloser
    >
      <Stack gap={"xl"}>
        {categories && (
          <Select
            label={t("time_off:category")}
            options={categories}
            emptyTitle={t("common:select")}
            value={selectedCategoryId}
            onChange={(value) => setSelectedCategoryId(Number(value))}
          />
        )}
        {!!selectedCategoryId &&
          (isPending ? (
            <Stack gap={"md"}>
              <Skeleton className={"h-12 w-full"} />
              <Skeleton className={"h-12 w-full"} />
            </Stack>
          ) : (
            checks && (
              <Stack gap={"md"}>
                <Text className={"text-secondary"}>{renderHint(checks)}</Text>
                <Stack>
                  {checks.map((check, index) => (
                    <ListItem
                      key={check.policy_id}
                      to={`/settings/time-off/categories/${selectedCategoryId}/policies/${check.policy_id}`}
                      blank={true}
                      truncate={true}
                      title={`${index + 1}. ${check.title}`}
                      captionSlot={renderRules(check)}
                      valueSlot={renderStatus(check, index)}
                    />
                  ))}
                </Stack>
                {isNoneAssigned && (
                  <ButtonLink
                    href={`/settings/time-off/categories/${selectedCategoryId}`}
                    target={"_blank"}
                    size={"sm"}
                    variant={"secondary"}
                    className={"self-start"}
                    rightIcon={<FontAwesomeIcon icon="fa-light fa-arrow-up-right-from-square" />}
                  >
                    {t("time_off:policy_checks_open_category")}
                  </ButtonLink>
                )}
              </Stack>
            )
          ))}
      </Stack>
    </Modal>
  );
}
